import React, { useState } from 'react';
import { FlexibleCRUDTable } from '../../components/common/FlexibleCRUDTable';
import { companyEntity } from '../../config/entities/companies';
import { crmService } from '../../services/crmCRUD';
import { useLanguage } from '../../state/language/LanguageProvider';
import { ViewModal } from '../../components/common/ViewModal';
import { EditModal } from '../../components/common/EditModal';

export const CRMPage: React.FC = () => {
  const { t } = useLanguage();
  
  // Modal state management
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createdCompany, setCreatedCompany] = useState<any>(null);
  
  // Total count state - removed as it's not currently used in the UI
  // const [totalCompanies, setTotalCompanies] = useState<number>(0);
  
  // Load total companies count - removed as totalCompanies is not used
  // useEffect(() => {
  //   const loadTotalCount = async () => {
  //     try {
  //       // Use a very high limit to ensure we get all records and the correct total
  //       const response = await crmService.getCompanies({ limit: 99999 });
  //       if (response && response.pagination && response.pagination.total) {
  //         setTotalCompanies(response.pagination.total);
  //       } else if (response && typeof response === 'object' && 'total' in response) {
  //         setTotalCompanies(response.total);
  //       } else if (Array.isArray(response)) {
  //         setTotalCompanies(response.length);
  //       }
  //     } catch (error) {
  //       console.error('Error loading total companies count:', error);
  //     }
  //   };
  //   
  //   loadTotalCount();
  // }, []);

  // Use Dedicated CRM Service (same as dashboard) with wrapper for FlexibleCRUDTable compatibility
  const companyService = {
    getAll: (params: any) => crmService.getCompanies(params),
    get: (id: string) => crmService.getCompanyById(id),
    create: (data: any) => crmService.createCompany(data),
    update: (id: string, data: any) => crmService.updateCompany(id, data),
    delete: (id: string) => crmService.deleteCompany(id)
  };

  // CRUD handlers
  const handleViewCompany = (company: any) => {
    setSelectedCompany(company);
    setIsViewModalOpen(true);
  };

  const handleEditCompany = (company: any) => {
    setSelectedCompany(company);
    setIsEditModalOpen(true);
  };

  const handleDeleteCompany = async (company: any) => {
    await companyService.delete(company.id);
  };

  const handleCreateCompany = () => {
    setIsCreateModalOpen(true);
  };

  const handleEditSuccess = async (data: any) => {
    try {
      await companyService.update(selectedCompany.id, data);
      setIsEditModalOpen(false);
      setSelectedCompany(null);
      // The table will automatically refresh
    } catch (error) {
      console.error('Error updating company:', error);
      throw error; // Re-throw so the modal can handle the error
    }
  };

  const handleCreateSuccess = async (company: any) => {
    try {
      const created = await companyService.create(company);
      setCreatedCompany(created);
      setIsCreateModalOpen(false);
      // The table will automatically refresh
    } catch (error) {
      console.error('Error creating company:', error);
      throw error; // Re-throw so the modal can handle the error
    }
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedCompany(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedCompany(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  return (
    <>
      {/* Main Content */}
      <FlexibleCRUDTable
        entity={companyEntity}
        service={companyService}
        onView={handleViewCompany}
        onEdit={handleEditCompany}
        onDelete={handleDeleteCompany}
        onCreate={handleCreateCompany}
        externalCreatedItem={createdCompany}
        onOperationComplete={() => {
          // Table will automatically refresh after operations
        }}
        searchPlaceholder={t('crm.companies.searchPlaceholder')}
        showPagination={true}
        showSearch={true}
        pageSize={10}
      />

      {/* View Company Modal */}
      {isViewModalOpen && selectedCompany && (
        <ViewModal
          isOpen={isViewModalOpen}
          onClose={handleCloseViewModal}
          title={t('crm.companies.viewTitle', { entity: companyEntity.displayNameSingular || 'Company' })}
          data={selectedCompany}
          fields={companyEntity.fields.filter(f => !f.hidden).map(f => ({
            key: f.key || f.name || '',
            label: f.label,
            type: f.type
          }))}
        />
      )}

      {/* Edit Company Modal */}
      {isEditModalOpen && selectedCompany && (
        <EditModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          title={t('crm.companies.editTitle', { entity: companyEntity.displayNameSingular || 'Company' })}
          entity={companyEntity}
          initialData={selectedCompany}
          onSubmit={handleEditSuccess}
          onCancel={handleCloseEditModal}
          loading={false}
        />
      )}

      {/* Create Company Modal */}
      {isCreateModalOpen && (
        <EditModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          title={t('crm.companies.createTitle', { entity: companyEntity.displayNameSingular || 'Company' })}
          entity={companyEntity}
          onSubmit={handleCreateSuccess}
          onCancel={handleCloseCreateModal}
          loading={false}
        />
      )}
    </>
  );
};